import type { BlockRootContext, BlockRootContextPropertyResult } from '#layers/block-engine/types/block';
import type { BlockRootContextApi } from '#layers/block-engine/utils/block/api/root-context';
import { tryOnScopeDispose } from '@vueuse/core';
import { computed, markRaw, shallowRef } from 'vue';
import { defineStore } from '#imports';

export interface BlockRootContextRegistration {
	/**
	 * Unique render instance identifier.
	 */
	renderId: string;
	/**
	 * Returns the current root context for the render instance.
	 */
	getRootContext: () => BlockRootContext;
	/**
	 * Returns the published root context properties for this render instance.
	 */
	getRootContextProperty: BlockRootContextApi['getRootContextProperty'];
}

interface GetContextPropertyOptions {
	filter?: (rootContext: BlockRootContext) => boolean;
}

export interface RegisterBlockRootContextSourceOptions {
	/**
	 * Unique render instance identifier.
	 */
	renderId: string;
	/**
	 * Root context API owned by the render boundary.
	 */
	rootContextApi: BlockRootContextApi;
}

export const useBlockRootContextStore = defineStore('block-root-context', () => {
	const registrations = shallowRef<BlockRootContextRegistration[]>([]);

	const rootContexts = computed(() => registrations.value.map((registration) => registration.getRootContext()));

	function registerRootContext(registration: BlockRootContextRegistration) {
		unregisterRootContext(registration.renderId);

		registrations.value = [...registrations.value, markRaw(registration)];

		const unregister = () => {
			unregisterRootContext(registration.renderId);
		};

		tryOnScopeDispose(unregister);

		return {
			unregister,
		};
	}

	function unregisterRootContext(renderId: string) {
		registrations.value = registrations.value.filter((registration) => registration.renderId !== renderId);
	}

	function getContextProperty<TValue = unknown>(key: string, options?: GetContextPropertyOptions): Array<BlockRootContextPropertyResult<TValue>> {
		return registrations.value.flatMap((registration) => {
			if (options?.filter && !options.filter(registration.getRootContext())) {
				return [];
			}

			return registration.getRootContextProperty<TValue>(key);
		});
	}

	return {
		rootContexts,
		registerRootContext,
		unregisterRootContext,
		getContextProperty,
	};
});

/**
 * Registers a render boundary as a root context source in the global store.
 */
export function registerBlockRootContextSource(options: RegisterBlockRootContextSourceOptions) {
	return useBlockRootContextStore().registerRootContext({
		renderId: options.renderId,
		getRootContext: options.rootContextApi.getRootContext,
		getRootContextProperty: options.rootContextApi.getRootContextProperty,
	});
}
